/**
 * BIM Viewer Component
 *
 * 3D viewer for BIM/CAD files using Three.js
 * Supports: IFC, GLTF, GLB, OBJ, DXF
 */

'use client';

import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { type DesignFile, type BIMElement } from './types';
import { Skeleton } from '@/components/base/Skeleton';

// Scene object info for external tracking
export interface SceneObjectInfo {
  id: string;
  name: string;
  type: 'mesh' | 'line' | 'group' | 'helper' | 'other';
  visible: boolean;
  vertexCount: number;
  fileId?: string;
}

interface BIMViewerProps {
  files: DesignFile[];
  selectedFileId?: string;
  onElementSelect?: (element: BIMElement) => void;
  onConversionComplete?: (file: DesignFile, convertedUrl: string, stats: any) => void;
  onObjectsLoaded?: (objects: SceneObjectInfo[]) => void;
  height?: string | number;
  mode?: '3d' | '2d';
}

// Exposed methods via ref
export interface BIMViewerHandle {
  toggleObjectVisibility: (objectId: string) => void;
  zoomToObject: (objectId: string) => void;
  getSceneObjects: () => SceneObjectInfo[];
}

// Conversion status for CAD files
interface ConversionState {
  [fileId: string]: {
    status: 'checking' | 'converting' | 'converted' | 'failed' | 'not_needed';
    convertedUrl?: string;
    format?: 'obj' | 'svg';
    error?: string;
    stats?: { vertices: number; faces: number; edges: number; entities?: number };
  };
}

// SVG viewer state
interface SvgViewerState {
  url: string | null;
  zoom: number;
  panX: number;
  panY: number;
  isDragging: boolean;
  lastMouseX: number;
  lastMouseY: number;
}

// Dynamically import Three.js to avoid SSR issues
let THREE: any = null;
let OrbitControls: any = null;
let GLTFLoader: any = null;
let DXFLoader: any = null;

const BIMViewer = forwardRef<BIMViewerHandle, BIMViewerProps>(({
  files,
  selectedFileId,
  onElementSelect,
  onConversionComplete,
  onObjectsLoaded,
  height = '100%',
  mode = '3d'
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<any>(null);
  const sceneRef = useRef<any>(null);
  const cameraRef = useRef<any>(null);
  const controlsRef = useRef<any>(null);
  const animationFrameRef = useRef<number>();
  const modelsRef = useRef<Map<string, any>>(new Map());

  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selectedElement, setSelectedElement] = useState<BIMElement | null>(null);
  const [isThreeLoaded, setIsThreeLoaded] = useState(false);

  // Stats
  const [stats, setStats] = useState({ vertices: 0, faces: 0, objects: 0, lines: 0, entities: 0 });

  // Scene objects for external tracking
  const [sceneObjects, setSceneObjects] = useState<SceneObjectInfo[]>([]);

  // SVG viewer state
  const [svgViewer, setSvgViewer] = useState<SvgViewerState>({
    url: null,
    zoom: 1,
    panX: 0,
    panY: 0,
    isDragging: false,
    lastMouseX: 0,
    lastMouseY: 0
  });
  const svgContainerRef = useRef<HTMLDivElement>(null);

  // Collect scene objects for external display
  const collectSceneObjects = useCallback((): SceneObjectInfo[] => {
    if (!sceneRef.current) return [];

    const objects: SceneObjectInfo[] = [];

    sceneRef.current.traverse((object: any) => {
      // Skip the scene itself
      if (object === sceneRef.current) return;

      // Determine object type
      let type: SceneObjectInfo['type'] = 'other';
      if (object.isGridHelper || object.isAxesHelper || object.type?.includes('Helper')) {
        type = 'helper';
      } else if (object.isMesh) {
        type = 'mesh';
      } else if (object.isLine || object.isLineSegments) {
        type = 'line';
      } else if (object.isGroup) {
        type = 'group';
      }

      // Skip certain internal objects
      if (object.type === 'AmbientLight' || object.type === 'DirectionalLight') return;

      // Get vertex count
      let vertexCount = 0;
      if (object.geometry?.attributes?.position) {
        vertexCount = object.geometry.attributes.position.count;
      }

      objects.push({
        id: object.uuid,
        name: object.name || object.type || 'Unknown',
        type,
        visible: object.visible,
        vertexCount,
      });
    });

    return objects;
  }, []);

  // Toggle object visibility
  const toggleObjectVisibility = useCallback((objectId: string) => {
    if (!sceneRef.current) return;

    sceneRef.current.traverse((object: any) => {
      if (object.uuid === objectId) {
        object.visible = !object.visible;
      }
    });

    // Update scene objects state
    setSceneObjects(collectSceneObjects());
  }, [collectSceneObjects]);

  // Zoom to object
  const zoomToObject = useCallback((objectId: string) => {
    if (!sceneRef.current || !cameraRef.current || !controlsRef.current) return;

    sceneRef.current.traverse((object: any) => {
      if (object.uuid === objectId && object.geometry) {
        object.geometry.computeBoundingBox();
        const box = object.geometry.boundingBox.clone();
        object.updateWorldMatrix(true, false);
        box.applyMatrix4(object.matrixWorld);

        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = cameraRef.current.fov * (Math.PI / 180);
        const distance = Math.abs(maxDim / 2 / Math.tan(fov / 2)) * 1.5;

        cameraRef.current.position.set(center.x + distance, center.y + distance / 2, center.z + distance);
        controlsRef.current.target.copy(center);
        controlsRef.current.update();
      }
    });
  }, []);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    toggleObjectVisibility,
    zoomToObject,
    getSceneObjects: collectSceneObjects,
  }), [toggleObjectVisibility, zoomToObject, collectSceneObjects]);

  // CAD conversion state
  const [conversionState, setConversionState] = useState<ConversionState>({});
  const [conversionMessage, setConversionMessage] = useState<string | null>(null);

  // Load Three.js dynamically
  useEffect(() => {
    const loadThree = async () => {
      try {
        const threeModule = await import('three');
        THREE = threeModule;

        const { OrbitControls: OC } = await import('three/examples/jsm/controls/OrbitControls.js');
        OrbitControls = OC;

        const { GLTFLoader: GL } = await import('three/examples/jsm/loaders/GLTFLoader.js');
        GLTFLoader = GL;

        setIsThreeLoaded(true);
      } catch (err) {
        console.error('Failed to load Three.js:', err);
        setError('Failed to load 3D viewer');
      }
    };

    loadThree();
  }, []);

  // Initialize scene
  useEffect(() => {
    if (!isThreeLoaded || !containerRef.current) return;

    // Create scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);
    sceneRef.current = scene;

    // Create camera
    const camera = new THREE.PerspectiveCamera(
      60,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      10000
    );
    camera.position.set(50, 50, 50);
    cameraRef.current = camera;

    // Create renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true
    });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Add orbit controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.screenSpacePanning = true;
    controls.minDistance = 1;
    controls.maxDistance = 1000;
    controlsRef.current = controls;

    // Add lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(50, 50, 50);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    scene.add(directionalLight);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
    directionalLight2.position.set(-50, 50, -50);
    scene.add(directionalLight2);

    // Add grid
    const gridHelper = new THREE.GridHelper(200, 50, 0x444444, 0x333333);
    scene.add(gridHelper);

    // Add axes helper
    const axesHelper = new THREE.AxesHelper(20);
    scene.add(axesHelper);

    // Animation loop
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);

    // Raycaster for picking
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const handleClick = (event: MouseEvent) => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(scene.children, true);

      if (intersects.length > 0) {
        const object = intersects[0].object;
        const element: BIMElement = {
          id: object.uuid,
          name: object.name || 'Unknown',
          type: object.type,
          properties: object.userData
        };
        setSelectedElement(element);
        onElementSelect?.(element);

        // Highlight selected object
        if ((object as any).material) {
          const originalColor = (object as any).material.color?.clone();
          (object as any).material.emissive = new THREE.Color(0x4444ff);
          setTimeout(() => {
            if ((object as any).material.emissive) {
              (object as any).material.emissive = new THREE.Color(0x000000);
            }
          }, 200);
        }
      }
    };
    renderer.domElement.addEventListener('click', handleClick);

    setIsLoading(false);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      renderer.domElement.removeEventListener('click', handleClick);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      renderer.dispose();
      if (containerRef.current?.contains(renderer.domElement)) {
        containerRef.current.removeChild(renderer.domElement);
      }
    };
  }, [isThreeLoaded, onElementSelect]);

  // Check conversion status and trigger conversion if needed for DWG files
  const checkAndConvertCAD = async (file: DesignFile): Promise<{ url: string; format?: string; stats?: any } | null> => {
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    // Only handle DWG files that need conversion
    if (ext !== '.dwg') {
      return null;
    }

    console.log('DWG file conversion check:', {
      id: file.id,
      name: file.name,
      designId: file.designId,
      drawingId: file.drawingId,
      convertedUrl: file.convertedUrl
    });

    // If already have converted URL in file metadata
    if (file.convertedUrl) {
      setConversionMessage(null);
      // Include conversionFormat from file metadata for proper rendering detection
      const format = file.conversionFormat || (file.convertedUrl.endsWith('.svg') ? 'svg' : 'obj');
      return { url: file.convertedUrl, format, stats: file.conversionStats };
    }

    // Need designId and drawingId for conversion
    if (!file.designId || !file.drawingId) {
      console.warn('DWG file missing designId or drawingId for conversion:', file.name, {
        designId: file.designId,
        drawingId: file.drawingId
      });
      setError(`DWG conversion requires design to be saved first. Please save and try again.`);
      return null;
    }

    setConversionState(prev => ({
      ...prev,
      [file.id]: { status: 'checking' }
    }));
    setConversionMessage(`Checking if ${file.name} needs conversion...`);

    try {
      // Check if already converted
      const statusResponse = await fetch(`/apiv1/ds-wms/designs/${file.designId}/drawings/${file.drawingId}/conversion-status`);
      const statusData = await statusResponse.json();

      if (statusData.success && statusData.data?.converted) {
        const convertedUrl = statusData.data.outputUrl;
        const format = statusData.data.format || (convertedUrl?.endsWith('.svg') ? 'svg' : 'obj');
        setConversionState(prev => ({
          ...prev,
          [file.id]: { status: 'converted', convertedUrl, format, stats: statusData.data.stats }
        }));
        setConversionMessage(null);

        // Notify parent
        onConversionComplete?.(file, convertedUrl, statusData.data.stats);

        return { url: convertedUrl, format, stats: statusData.data.stats };
      }

      // Not converted yet, trigger conversion
      setConversionState(prev => ({
        ...prev,
        [file.id]: { status: 'converting' }
      }));
      setConversionMessage(`Converting to Digital Twin format for viewing (one-time activity)...`);

      const convertResponse = await fetch(`/apiv1/ds-wms/designs/${file.designId}/drawings/${file.drawingId}/convert`, {
        method: 'POST'
      });
      const convertData = await convertResponse.json();

      if (convertData.success && convertData.data?.outputUrl) {
        const convertedUrl = convertData.data.outputUrl;
        const format = convertData.data.format || (convertedUrl?.endsWith('.svg') ? 'svg' : 'obj');
        setConversionState(prev => ({
          ...prev,
          [file.id]: { status: 'converted', convertedUrl, format, stats: convertData.data.stats }
        }));
        setConversionMessage(null);

        // Notify parent
        onConversionComplete?.(file, convertedUrl, convertData.data.stats);

        return { url: convertedUrl, format, stats: convertData.data.stats };
      } else {
        const errorMsg = convertData.data?.error || convertData.message || 'Conversion failed';
        setConversionState(prev => ({
          ...prev,
          [file.id]: { status: 'failed', error: errorMsg }
        }));
        setConversionMessage(null);
        setError(errorMsg);
        return null;
      }
    } catch (err) {
      console.error('Conversion error:', err);
      setConversionState(prev => ({
        ...prev,
        [file.id]: { status: 'failed', error: String(err) }
      }));
      setConversionMessage(null);
      return null;
    }
  };

  // Load files
  useEffect(() => {
    if (!isThreeLoaded || !sceneRef.current) return;

    const loadFile = async (file: DesignFile) => {
      if (modelsRef.current.has(file.id)) return;

      setIsLoading(true);
      setLoadingProgress(0);

      try {
        const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

        let model: any;
        let fileUrl = file.url;

        // Handle DWG files - check for conversion
        if (ext === '.dwg') {
          const conversionResult = await checkAndConvertCAD(file);
          if (conversionResult) {
            // Check if SVG format (2D CAD)
            if (conversionResult.url.endsWith('.svg') || conversionResult.format === 'svg') {
              console.log('Loading SVG for 2D CAD display:', conversionResult.url);
              // Use SVG viewer for 2D drawings
              setSvgViewer(prev => ({
                ...prev,
                url: conversionResult.url,
                zoom: 1,
                panX: 0,
                panY: 0
              }));
              if (conversionResult.stats) {
                setStats({
                  vertices: conversionResult.stats.vertices || 0,
                  faces: conversionResult.stats.faces || 0,
                  objects: 1,
                  lines: conversionResult.stats.edges || 0,
                  entities: conversionResult.stats.entities || 0
                });
              }
              setIsLoading(false);
              setLoadingProgress(100);
              return; // Don't load in Three.js
            }

            // Load the converted OBJ file for 3D
            model = await loadOBJ(conversionResult.url);
            if (conversionResult.stats) {
              setStats({
                vertices: conversionResult.stats.vertices || 0,
                faces: conversionResult.stats.faces || 0,
                objects: 1,
                lines: conversionResult.stats.edges || 0,
                entities: conversionResult.stats.entities || 0
              });
            }
          } else {
            // Conversion not available or failed
            model = createPlaceholder(file.name + ' (DWG - needs conversion)');
          }
        } else if (ext === '.gltf' || ext === '.glb') {
          model = await loadGLTF(fileUrl);
        } else if (ext === '.obj') {
          model = await loadOBJ(fileUrl);
        } else if (ext === '.ifc') {
          model = await loadIFC(fileUrl);
        } else if (ext === '.dxf') {
          model = await loadDXF(fileUrl);
        } else {
          // Create placeholder
          model = createPlaceholder(file.name);
        }

        if (model) {
          model.name = file.name;
          model.userData = { fileId: file.id, fileName: file.name };
          sceneRef.current.add(model);
          modelsRef.current.set(file.id, model);

          // Center and fit camera
          fitCameraToObject(model);

          // Update stats
          updateStats();

          // Report scene objects to parent
          const objects = collectSceneObjects();
          console.log('Scene objects collected:', objects.length, objects);
          setSceneObjects(objects);
          onObjectsLoaded?.(objects);
        }
      } catch (err) {
        console.error('Error loading file:', file.name, err);
        setError(`Failed to load ${file.name}`);
      } finally {
        setIsLoading(false);
        setLoadingProgress(100);
      }
    };

    // Load all files
    files.forEach(file => loadFile(file));
  }, [files, isThreeLoaded]);

  // Load GLTF/GLB
  const loadGLTF = (url: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      const loader = new GLTFLoader();
      loader.load(
        url,
        (gltf: any) => resolve(gltf.scene),
        (progress: any) => {
          if (progress.total) {
            setLoadingProgress((progress.loaded / progress.total) * 100);
          }
        },
        reject
      );
    });
  };

  // Load OBJ
  const loadOBJ = async (url: string): Promise<any> => {
    const { OBJLoader } = await import('three/examples/jsm/loaders/OBJLoader.js');
    return new Promise((resolve, reject) => {
      const loader = new OBJLoader();
      loader.load(
        url,
        (object: any) => {
          console.log('OBJ loaded, children:', object.children.length);

          // Log all object types for debugging
          object.traverse((child: any) => {
            console.log('Child type:', child.type, 'isLine:', child.isLine, 'isLineSegments:', child.isLineSegments, 'isMesh:', child.isMesh);
          });

          // Apply visible material to line geometry - bright white/yellow for max visibility
          const lineMaterial = new THREE.LineBasicMaterial({
            color: 0xffff00, // Bright yellow for visibility
            linewidth: 2,
            depthTest: true,
            depthWrite: true
          });

          let meshCount = 0;
          let lineCount = 0;

          // Apply materials and fix visibility for all children
          // Check both property flags AND type string for maximum compatibility
          object.traverse((child: any) => {
            const isLineType = child.isLine || child.isLineSegments ||
                               child.type === 'Line' || child.type === 'LineSegments' ||
                               child.type === 'LineLoop';

            if (isLineType) {
              lineCount++;
              child.material = lineMaterial;
              // Compute bounding box for the geometry
              if (child.geometry) {
                child.geometry.computeBoundingBox();
                const bb = child.geometry.boundingBox;
                console.log('Line geometry bounds:',
                  'min:', bb?.min?.x, bb?.min?.y, bb?.min?.z,
                  'max:', bb?.max?.x, bb?.max?.y, bb?.max?.z);
              }
              console.log('Line found:', child.type, 'vertices:', child.geometry?.attributes?.position?.count);
            } else if (child.isMesh || child.type === 'Mesh') {
              meshCount++;
              if (!child.material) {
                child.material = new THREE.MeshStandardMaterial({
                  color: 0x4a90d9,
                  side: THREE.DoubleSide
                });
              }
            }
          });

          console.log('OBJ summary - Meshes:', meshCount, 'Lines:', lineCount);

          resolve(object);
        },
        undefined,
        reject
      );
    });
  };

  // Load IFC (placeholder - requires web-ifc)
  const loadIFC = async (url: string): Promise<any> => {
    // IFC loading requires web-ifc library
    // For now, create a placeholder
    console.log('IFC loading requires web-ifc library. URL:', url);
    return createPlaceholder('IFC Model (web-ifc required)');
  };

  // Load DXF (placeholder - requires dxf-parser)
  const loadDXF = async (url: string): Promise<any> => {
    // DXF loading requires dxf-parser library
    console.log('DXF loading requires dxf-parser library. URL:', url);
    return createPlaceholder('DXF Model (dxf-parser required)');
  };

  // Create placeholder geometry
  const createPlaceholder = (name: string): any => {
    const group = new THREE.Group();
    group.name = name;

    // Create a simple building placeholder
    const buildingGeometry = new THREE.BoxGeometry(20, 30, 15);
    const buildingMaterial = new THREE.MeshStandardMaterial({
      color: 0x4a90d9,
      transparent: true,
      opacity: 0.8
    });
    const building = new THREE.Mesh(buildingGeometry, buildingMaterial);
    building.position.y = 15;
    building.castShadow = true;
    building.receiveShadow = true;
    group.add(building);

    // Add roof
    const roofGeometry = new THREE.ConeGeometry(14, 8, 4);
    const roofMaterial = new THREE.MeshStandardMaterial({ color: 0x8b4513 });
    const roof = new THREE.Mesh(roofGeometry, roofMaterial);
    roof.position.y = 34;
    roof.rotation.y = Math.PI / 4;
    group.add(roof);

    return group;
  };

  // Fit camera to object
  const fitCameraToObject = (object: any) => {
    if (!cameraRef.current || !controlsRef.current) return;

    // Compute bounding box manually to handle Line geometry properly
    const box = new THREE.Box3();
    object.traverse((child: any) => {
      if (child.geometry) {
        child.geometry.computeBoundingBox();
        if (child.geometry.boundingBox) {
          const childBox = child.geometry.boundingBox.clone();
          child.updateWorldMatrix(true, false);
          childBox.applyMatrix4(child.matrixWorld);
          box.union(childBox);
        }
      }
    });

    // Fallback if bounding box is empty
    if (box.isEmpty()) {
      box.setFromObject(object);
    }

    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());

    console.log('Model bounds - center:', center.x, center.y, center.z, 'size:', size.x, size.y, size.z);
    console.log('Bounding box: min:', box.min.x, box.min.y, box.min.z, 'max:', box.max.x, box.max.y, box.max.z);

    const maxDim = Math.max(size.x, size.y, size.z) || 100;
    const fov = cameraRef.current.fov * (Math.PI / 180);
    let cameraDistance = Math.abs(maxDim / 2 / Math.tan(fov / 2));
    cameraDistance *= 1.2; // Closer zoom for better visibility

    // Check if this is essentially a 2D drawing (flat in Y axis)
    const is2D = size.y < 1 || (size.y / Math.max(size.x, size.z, 1)) < 0.05;

    console.log('Is 2D drawing:', is2D, 'Camera distance:', cameraDistance);

    if (is2D) {
      // Position camera looking down at the XZ plane for 2D CAD drawings
      cameraRef.current.position.set(center.x, cameraDistance, center.z);
      cameraRef.current.up.set(0, 0, -1);
      cameraRef.current.lookAt(center);
    } else {
      // Standard 3D perspective view
      cameraRef.current.position.set(center.x + cameraDistance, center.y + cameraDistance / 2, center.z + cameraDistance);
    }

    controlsRef.current.target.copy(center);
    controlsRef.current.update();
  };

  // Update stats
  const updateStats = () => {
    if (!sceneRef.current) return;

    let vertices = 0;
    let faces = 0;
    let objects = 0;
    let lines = 0;

    sceneRef.current.traverse((object: any) => {
      if (object.isMesh) {
        objects++;
        if (object.geometry) {
          vertices += object.geometry.attributes?.position?.count || 0;
          faces += (object.geometry.index?.count || 0) / 3;
        }
      } else if (object.isLine || object.isLineSegments) {
        objects++;
        if (object.geometry) {
          vertices += object.geometry.attributes?.position?.count || 0;
          // Count line segments (pairs of vertices)
          lines += Math.floor((object.geometry.attributes?.position?.count || 0) / 2);
        }
      }
    });

    setStats({ vertices, faces: Math.floor(faces), objects, lines, entities: 0 });
  };

  // View controls
  const resetView = () => {
    if (cameraRef.current && controlsRef.current) {
      cameraRef.current.position.set(50, 50, 50);
      controlsRef.current.target.set(0, 0, 0);
      controlsRef.current.update();
    }
  };

  const setTopView = () => {
    if (cameraRef.current && controlsRef.current) {
      // Get the current target (model center) and position camera above it
      const target = controlsRef.current.target.clone();
      cameraRef.current.position.set(target.x, 150, target.z);
      cameraRef.current.up.set(0, 0, -1); // Z-up for proper 2D CAD orientation
      cameraRef.current.lookAt(target);
      controlsRef.current.update();
    }
  };

  const setFrontView = () => {
    if (cameraRef.current && controlsRef.current) {
      cameraRef.current.position.set(0, 25, 100);
      controlsRef.current.target.set(0, 25, 0);
      controlsRef.current.update();
    }
  };

  const setSideView = () => {
    if (cameraRef.current && controlsRef.current) {
      cameraRef.current.position.set(100, 25, 0);
      controlsRef.current.target.set(0, 25, 0);
      controlsRef.current.update();
    }
  };

  if (!isThreeLoaded) {
    return (
      <div className="w-full h-full bg-gray-900 p-4">
        <Skeleton shape="rounded" className="h-full w-full" />
      </div>
    );
  }

  // SVG pan/zoom handlers
  const handleSvgWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setSvgViewer(prev => ({
      ...prev,
      zoom: Math.min(Math.max(prev.zoom * delta, 0.1), 10)
    }));
  };

  const handleSvgMouseDown = (e: React.MouseEvent) => {
    setSvgViewer(prev => ({
      ...prev,
      isDragging: true,
      lastMouseX: e.clientX,
      lastMouseY: e.clientY
    }));
  };

  const handleSvgMouseMove = (e: React.MouseEvent) => {
    if (!svgViewer.isDragging) return;
    const dx = e.clientX - svgViewer.lastMouseX;
    const dy = e.clientY - svgViewer.lastMouseY;
    setSvgViewer(prev => ({
      ...prev,
      panX: prev.panX + dx,
      panY: prev.panY + dy,
      lastMouseX: e.clientX,
      lastMouseY: e.clientY
    }));
  };

  const handleSvgMouseUp = () => {
    setSvgViewer(prev => ({ ...prev, isDragging: false }));
  };

  const resetSvgView = () => {
    setSvgViewer(prev => ({ ...prev, zoom: 1, panX: 0, panY: 0 }));
  };

  return (
    <div className="relative w-full h-full bg-gray-900" style={{ height }}>
      {/* SVG Viewer for 2D CAD */}
      {svgViewer.url && (
        <div
          ref={svgContainerRef}
          className="absolute inset-0 overflow-hidden cursor-grab active:cursor-grabbing"
          onWheel={handleSvgWheel}
          onMouseDown={handleSvgMouseDown}
          onMouseMove={handleSvgMouseMove}
          onMouseUp={handleSvgMouseUp}
          onMouseLeave={handleSvgMouseUp}
        >
          <object
            data={svgViewer.url}
            type="image/svg+xml"
            className="w-full h-full"
            style={{
              transform: `translate(${svgViewer.panX}px, ${svgViewer.panY}px) scale(${svgViewer.zoom})`,
              transformOrigin: 'center center',
              transition: svgViewer.isDragging ? 'none' : 'transform 0.1s ease-out'
            }}
          >
            <img src={svgViewer.url} alt="CAD Drawing" className="w-full h-full object-contain" />
          </object>
        </div>
      )}

      {/* 3D Canvas Container - hidden when showing SVG */}
      <div ref={containerRef} className={`absolute inset-0 ${svgViewer.url ? 'hidden' : ''}`} />

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-gray-900/80 flex flex-col items-center justify-center gap-3 z-10">
          <Skeleton shape="rounded" className="h-3/5 w-4/5" />
          {/* Determinate progress stays — it carries real information the
              skeleton cannot. */}
          <div className="w-48 h-1 bg-gray-700 rounded-full">
            <div
              className="h-full bg-indigo-500 rounded-full transition-all"
              style={{ width: `${loadingProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Conversion Modal - Prominent display for DWG conversion */}
      {conversionMessage && (
        <div className="absolute inset-0 bg-gray-900/90 flex items-center justify-center z-20">
          <div className="bg-gray-800 rounded-xl p-8 max-w-md text-center shadow-2xl border border-gray-700">
            <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-refresh-line text-3xl text-white animate-spin"></i>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              Converting CAD File
            </h3>
            <p className="text-gray-300 mb-4">
              {conversionMessage}
            </p>
            <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-500 rounded-full animate-pulse" style={{ width: '60%' }}></div>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              This is a one-time process. Future views will load instantly.
            </p>
          </div>
        </div>
      )}

      {/* Error - Enhanced for DWG conversion */}
      {error && (
        <div className="absolute inset-0 bg-gray-900/90 flex items-center justify-center z-20">
          <div className="bg-gray-800 rounded-xl p-6 max-w-lg text-center shadow-2xl border border-gray-700">
            <div className="w-14 h-14 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className={`${error.includes('DWG') || error.includes('ODA') || error.includes('LibreDWG') ? 'ri-file-warning-line' : 'ri-error-warning-line'} text-2xl text-white`}></i>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">
              {(error.includes('DWG') || error.includes('ODA') || error.includes('LibreDWG')) ? 'DWG File - Preview Not Available' : 'Error Loading File'}
            </h3>
            <p className="text-gray-300 mb-4 text-sm">
              {(error.includes('DWG') || error.includes('ODA') || error.includes('LibreDWG'))
                ? 'DWG files require server-side conversion tools that are not currently installed.'
                : error}
            </p>

            {(error.includes('DWG') || error.includes('ODA') || error.includes('LibreDWG')) && (
              <div className="space-y-3 mb-4">
                {/* Download option */}
                {files.filter(f => f.name.toLowerCase().endsWith('.dwg')).map(dwgFile => (
                  <a
                    key={dwgFile.id}
                    href={dwgFile.url}
                    download={dwgFile.name}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <i className="ri-download-line"></i>
                    Download {dwgFile.name}
                  </a>
                ))}

                <div className="bg-gray-700/50 rounded-lg p-4 text-left text-sm">
                  <p className="text-gray-400 mb-2 font-medium">Alternative options:</p>
                  <ul className="text-gray-300 space-y-2">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-green-400 mt-0.5"></i>
                      <span>Download and open with AutoCAD, DraftSight, or free viewers like A360</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-green-400 mt-0.5"></i>
                      <span>Convert to DXF format and re-upload</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-green-400 mt-0.5"></i>
                      <span>Ask admin to install ODA File Converter on server</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            <button
              onClick={() => setError(null)}
              className="px-4 py-2 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* View Controls - Different for SVG vs 3D */}
      <div className="absolute top-4 right-4 flex flex-col gap-1 z-10">
        {svgViewer.url ? (
          // SVG viewer controls
          <>
            <button
              onClick={resetSvgView}
              className="w-8 h-8 bg-gray-800/90 text-gray-300 hover:bg-gray-700 rounded flex items-center justify-center text-sm"
              title="Reset View"
            >
              <i className="ri-home-line"></i>
            </button>
            <button
              onClick={() => setSvgViewer(prev => ({ ...prev, zoom: Math.min(prev.zoom * 1.2, 10) }))}
              className="w-8 h-8 bg-gray-800/90 text-gray-300 hover:bg-gray-700 rounded flex items-center justify-center text-sm"
              title="Zoom In"
            >
              <i className="ri-zoom-in-line"></i>
            </button>
            <button
              onClick={() => setSvgViewer(prev => ({ ...prev, zoom: Math.max(prev.zoom * 0.8, 0.1) }))}
              className="w-8 h-8 bg-gray-800/90 text-gray-300 hover:bg-gray-700 rounded flex items-center justify-center text-sm"
              title="Zoom Out"
            >
              <i className="ri-zoom-out-line"></i>
            </button>
            <button
              onClick={() => setSvgViewer(prev => ({ ...prev, zoom: 1 }))}
              className="w-8 h-8 bg-gray-800/90 text-gray-300 hover:bg-gray-700 rounded flex items-center justify-center text-xs font-bold"
              title="Fit to View"
            >
              1:1
            </button>
          </>
        ) : (
          // 3D viewer controls
          <>
            <button
              onClick={resetView}
              className="w-8 h-8 bg-gray-800/90 text-gray-300 hover:bg-gray-700 rounded flex items-center justify-center text-sm"
              title="Reset View"
            >
              <i className="ri-home-line"></i>
            </button>
            <button
              onClick={setTopView}
              className="w-8 h-8 bg-gray-800/90 text-gray-300 hover:bg-gray-700 rounded flex items-center justify-center text-xs font-bold"
              title="Top View"
            >
              T
            </button>
            <button
              onClick={setFrontView}
              className="w-8 h-8 bg-gray-800/90 text-gray-300 hover:bg-gray-700 rounded flex items-center justify-center text-xs font-bold"
              title="Front View"
            >
              F
            </button>
            <button
              onClick={setSideView}
              className="w-8 h-8 bg-gray-800/90 text-gray-300 hover:bg-gray-700 rounded flex items-center justify-center text-xs font-bold"
              title="Side View"
            >
              S
            </button>
          </>
        )}
      </div>

      {/* Stats - Different for SVG vs 3D */}
      <div className="absolute bottom-4 left-4 bg-gray-800/90 text-gray-300 px-3 py-2 rounded text-xs z-10">
        <div className="flex items-center gap-4">
          {svgViewer.url ? (
            // SVG stats
            <>
              {stats.entities > 0 && <span><i className="ri-shape-line mr-1"></i>{stats.entities.toLocaleString()} entities</span>}
              {stats.vertices > 0 && <span><i className="ri-map-pin-line mr-1"></i>{stats.vertices.toLocaleString()} vertices</span>}
              {stats.lines > 0 && <span><i className="ri-pencil-ruler-line mr-1"></i>{stats.lines.toLocaleString()} lines</span>}
              <span className="text-indigo-400"><i className="ri-zoom-in-line mr-1"></i>{Math.round(svgViewer.zoom * 100)}%</span>
            </>
          ) : (
            // 3D stats
            <>
              <span><i className="ri-box-3-line mr-1"></i>{stats.objects} objects</span>
              <span><i className="ri-shape-line mr-1"></i>{stats.vertices.toLocaleString()} vertices</span>
              {stats.faces > 0 && <span><i className="ri-shape-2-line mr-1"></i>{stats.faces.toLocaleString()} faces</span>}
              {stats.lines > 0 && <span><i className="ri-pencil-ruler-line mr-1"></i>{stats.lines.toLocaleString()} lines</span>}
            </>
          )}
        </div>
      </div>

      {/* Selected Element Info */}
      {selectedElement && (
        <div className="absolute bottom-4 right-4 bg-gray-800/90 text-gray-300 px-4 py-3 rounded-lg text-sm max-w-xs z-10">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-white">{selectedElement.name}</span>
            <button
              onClick={() => setSelectedElement(null)}
              className="text-gray-400 hover:text-white"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
          <div className="text-xs space-y-1 text-gray-400">
            <div>Type: {selectedElement.type}</div>
            <div>ID: {selectedElement.id.substring(0, 8)}...</div>
          </div>
        </div>
      )}

      {/* Help */}
      <div className="absolute top-4 left-4 text-gray-500 text-xs z-10">
        <div className="bg-gray-800/70 px-2 py-1 rounded">
          {svgViewer.url ? (
            // SVG help
            <>
              <span className="mr-3"><i className="ri-drag-move-2-line mr-1"></i>Drag to Pan</span>
              <span><i className="ri-zoom-in-line mr-1"></i>Scroll to Zoom</span>
            </>
          ) : (
            // 3D help
            <>
              <span className="mr-3"><i className="ri-mouse-line mr-1"></i>Rotate</span>
              <span className="mr-3"><i className="ri-zoom-in-line mr-1"></i>Scroll</span>
              <span><i className="ri-hand-line mr-1"></i>Shift+Drag</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

BIMViewer.displayName = 'BIMViewer';

export default BIMViewer;
